import { Check, ContentCopy, LocalOffer } from '@mui/icons-material'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import React, { useCallback, useState } from 'react'

import LazyImage from '@/src/components/common/ui/LazyImage'
import { FlightOffer } from '@/src/types/flightOffer'

interface BannerCardProps {
    offer: FlightOffer
    onOfferClick: (slug: string | undefined) => void
    priority?: boolean
}

export const BannerCard: React.FC<BannerCardProps> = ({ offer, onOfferClick, priority }) => {
    const [copied, setCopied] = useState(false)

    const handleCopyCode = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation()
            if (offer.couponCode) {
                navigator.clipboard.writeText(offer.couponCode)
                setCopied(true)
                setTimeout(() => setCopied(false), 2000)
            }
        },
        [offer.couponCode]
    )

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                height: { xs: '260px', lg: '320px' },
                minHeight: { xs: '260px', lg: '320px' },
                maxHeight: { xs: '260px', lg: '320px' },
                borderRadius: 2,
                overflow: 'hidden',
                border: 1,
                borderColor: 'divider',
                bgcolor: 'common.white'
            }}
        >
            {/* Banner Image */}
            <Box
                onClick={() => onOfferClick(offer.slug)}
                sx={{
                    width: '100%',
                    height: { xs: '140px', lg: '200px' },
                    minHeight: { xs: '140px', lg: '200px' },
                    maxHeight: { xs: '140px', lg: '200px' },
                    aspectRatio: '16/9',
                    position: 'relative',
                    overflow: 'hidden',
                    bgcolor: 'grey.100',
                    flexShrink: 0,
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease-in-out',
                    '&:hover': {
                        transform: 'scale(1.05)'
                    }
                }}
            >
                <LazyImage
                    src={offer.imageUrl}
                    alt={offer.title || 'Offer banner'}
                    sizes="(max-width: 768px) 250px, (max-width: 1200px) 33vw, 400px"
                    objectFit="fill"
                    priority={priority}
                />
            </Box>

            {/* Banner Content */}
            <Box
                sx={{
                    bgcolor: 'common.white',
                    minHeight: { xs: '120px', lg: '120px' },
                    maxHeight: { xs: '120px', lg: '120px' },
                    height: { xs: '120px', lg: '120px' },
                    display: 'flex',
                    flexDirection: 'column',
                    px: 2,
                    py: 1.5,
                    flexShrink: 0,
                    justifyContent: 'space-between'
                }}
            >
                {/* Title and Description */}
                <Box
                    sx={{
                        flex: '1 1 auto',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        minHeight: { xs: '42px', md: '42px' },
                        gap: 0.5
                    }}
                >
                    {/* Title */}
                    {offer.title && (
                        <Typography
                            variant="body1"
                            sx={{
                                fontSize: 12,
                                color: '#6941C6',
                                textAlign: 'left',
                                lineHeight: 1,
                                fontWeight: 600,
                                display: '-webkit-box',
                                WebkitLineClamp: 1,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }}
                        >
                            {offer.title}
                        </Typography>
                    )}
                    {/* Description */}
                    <Typography
                        variant="body1"
                        sx={{
                            fontSize: 14,
                            color: 'text.primary',
                            textAlign: 'left',
                            lineHeight: 1.5,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            fontWeight: 600
                        }}
                    >
                        {offer.description || ''}
                    </Typography>
                </Box>

                {/* Coupon Code */}
                {offer.couponCode && (
                    <Box
                        sx={{
                            p: 0.75,
                            pr: 0.5,
                            borderRadius: 1,
                            border: '1px dashed',
                            borderColor: 'grey.400',
                            bgcolor: 'grey.100',
                            display: 'flex',
                            alignItems: 'center',
                            gap: { xs: 0.5, md: 1 },
                            justifyContent: 'space-between',
                            flexShrink: 0
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 1 } }}>
                            <LocalOffer sx={{ fontSize: { xs: 14, md: 16 }, color: 'text.secondary' }} />
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    color: 'text.secondary',
                                    fontFamily: 'monospace',
                                    letterSpacing: 1,
                                    fontSize: { xs: '0.75rem', md: '0.875rem' }
                                }}
                            >
                                {offer.couponCode}
                            </Typography>
                        </Box>
                        <Tooltip title={copied ? 'Copied!' : 'Copy code'}>
                            <IconButton
                                size="small"
                                onClick={handleCopyCode}
                                sx={{
                                    p: 0.5,
                                    color: 'text.secondary',
                                    '&:hover': {
                                        bgcolor: 'grey.200'
                                    }
                                }}
                            >
                                {copied ? <Check sx={{ fontSize: 16 }} /> : <ContentCopy sx={{ fontSize: 16 }} />}
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            </Box>
        </Box>
    )
}

export default BannerCard
